/**
 * Staged copy for the Profile audit wait.
 *
 * A spinner on its own reads as a hung page, and this request is slow enough
 * for people to conclude exactly that: an audit makes seven reads in sequence
 * and returns nothing until the last one lands.
 *
 * Measured on this box, 2026-09-23, per step:
 *
 *   profile        ~1.0s      website probe  up to 8s (hard timeout)
 *   attributes     ~0.9s      photos         ~1.2s
 *   services       ~1.0s      posts          ~1.1s
 *   reviews         2.6s  (agileandco.com, 23 reviews)
 *                  11.1s  (kremerdental.com, 803)
 *                  23.2s  (mikehammermoving.com, 1,934)
 *
 *   totals: 7.7s small listing · 16.7s Kremer · ~35s Mike Hammer Moving
 *
 * Reviews dominate, and they dominate unpredictably, because the cost is the
 * listing's review count and we do not know it until we have read them.
 *
 * WHAT THIS MAY NOT DO. It may not claim progress. There is no job id and no
 * event stream behind the audit -- one blocking request, no partial results --
 * so a percentage, an ETA, or a ticked-off step list would be invented. The
 * stages below say what the audit DOES, in the order it does it, timed from
 * the measurements above; they never say a step has finished.
 *
 * Past the point where the measurements stop supporting a guess, the copy
 * stops advancing and says it is still going. Marching on to "Finalising…"
 * and then sitting there through a 30-second tail is the pattern that both
 * lies and tests worst (Harrison et al., CHI 2007: a late pause is perceived
 * as slower than no progress information at all).
 */

export type GbpLoadingTone = "normal" | "warning";

export interface GbpLoadingStage {
  headline: string;
  sub: string;
  tone: GbpLoadingTone;
}

/**
 * Past this, the longest listing we have measured should have returned.
 * Something is likely wrong, so we say so rather than keep reassuring.
 */
export const GBP_AUDIT_WARN_AFTER_SECONDS = 60;

interface Step {
  after: number;
  headline: string;
  sub: string;
  tone?: GbpLoadingTone;
}

const STEPS: Step[] = [
  {
    after: 0,
    headline: "Reading the profile from Google…",
    sub: "Business name, address, phone, categories and opening hours.",
  },
  {
    after: 2,
    headline: "Checking the website against the profile…",
    sub: "Fetching the client's own site to see whether it agrees with Google.",
  },
  {
    after: 5,
    headline: "Counting photos, posts and services…",
    sub: "What the listing actually shows a customer who lands on it.",
  },
  {
    after: 8,
    headline: "Reading every review…",
    sub:
      "The slow part. Google hands them over fifty at a time, so a listing " +
      "with a thousand reviews takes twenty round trips.",
  },
  {
    // The measurements stop supporting a guess here. Stop advancing.
    after: 25,
    headline: "Still reading reviews…",
    sub:
      "Large listings take longer — the biggest we handle has nearly two " +
      "thousand. Nothing has failed; the page will show the result when it lands.",
  },
  {
    after: GBP_AUDIT_WARN_AFTER_SECONDS,
    headline: "This is taking longer than it should.",
    sub:
      "Every listing we have measured returns well inside a minute. It may " +
      "still land, but if this keeps happening the audit is worth reporting.",
    tone: "warning",
  },
];

/** The stage for a given elapsed time. Last matching step wins. */
export function gbpAuditLoadingStage(elapsedSeconds: number): GbpLoadingStage {
  const secs = Number.isFinite(elapsedSeconds) ? Math.max(0, elapsedSeconds) : 0;
  let match = STEPS[0];
  for (const step of STEPS) {
    if (secs >= step.after) match = step;
  }
  return { headline: match.headline, sub: match.sub, tone: match.tone ?? "normal" };
}



/**
 * The wait BEFORE the audit panel exists.
 *
 * Switching client re-runs a page-level lookup of which Business Profiles the
 * client has, and the whole page is replaced by a spinner until it lands. The
 * audit's own staged copy cannot help here: the panel has not mounted yet.
 * That is why a tenant switch still looked like a hung page after the audit
 * wait was fixed (2026-09-23).
 *
 * Measured the same day, warm backend:
 *   agileandco.com 2.6s · kremerdental.com 3.2s · aaafence.com 2.9s
 *
 * Short, so the first line carries the whole job most of the time. The later
 * stages exist because "usually three seconds" is not "always".
 */
const LISTING_STEPS: Step[] = [
  {
    after: 0,
    headline: "Finding this client's Google listings…",
    sub: "Checking which Business Profiles are connected before the audit starts.",
  },
  {
    after: 8,
    headline: "Still looking…",
    sub: "This usually takes about three seconds. Nothing has failed.",
  },
  {
    after: 30,
    headline: "This is taking longer than it should.",
    sub:
      "The listing lookup normally returns in seconds. It may still land, " +
      "but if this keeps happening it is worth reporting.",
    tone: "warning",
  },
];

/** The stage for the listing lookup that runs before the audit. */
export function gbpListingLoadingStage(elapsedSeconds: number): GbpLoadingStage {
  const secs = Number.isFinite(elapsedSeconds) ? Math.max(0, elapsedSeconds) : 0;
  let match = LISTING_STEPS[0];
  for (const step of LISTING_STEPS) {
    if (secs >= step.after) match = step;
  }
  return { headline: match.headline, sub: match.sub, tone: match.tone ?? "normal" };
}
